import type { ISalesforceOrderRecordDTO } from '../../../../../api/salesforce-order.dao';

export const CANCELLATION_FIELD_KEYS = ['cancellationFee', 'cancellationReason', 'cancellationOther'];
export const POST_EVENT_FIELD_KEYS = ['qaChatEnabled', 'qaReportSent', 'eventRanSmoothly', 'postEventNotes'];

export const CANCELLATION_OTHER_KEY = 'cancellationOther';
export const CANCELLATION_REASON_KEY = 'cancellationReason';
export const CANCELLATION_REASON_OTHER = 'Other';
export const CONTRACT_NUMBER_FIELD_KEY = 'contractNumber';

export const HIGH_PROFILE_ORDER_KEY = 'highProfileOrder';
export const HIGH_PROFILE_REASONING_KEY = 'highProfileReasoning';
export const HIGH_PROFILE_ATTENDEE_DETAILS_KEY = 'highProfileAttendeeDetails';
export const HIGH_PROFILE_REASONING_ATTENDEE_VALUE = 'High Profile Attendee';

export const BILLING_METHOD_KEY = 'billingMethod';
export const BILLING_TIME_SENSITIVITY_KEY = 'billingTimeSensitivity';
export const BILLING_METHOD_BILL_PER_EVENT = 'Bill Per Event';

export const PROCESSED_FOR_ARCHIVE_KEY = 'processedForArchive';

export const CANCELLED_STATUSES = ['Cancelled'];
export const POST_EVENT_STATUSES = ['Delivered', 'Awaiting Confirmation', 'Complete'];

export const STANDARD_ORDER_RECORD_TYPE = 'Standard_Order';
export const NO_CONTRACT_REQUIRED_RECORD_TYPE_VALUE = 'No_Contract_Required_Order';

const CANCELLATION_KEY_SET = new Set<string>(CANCELLATION_FIELD_KEYS);
const POST_EVENT_KEY_SET = new Set<string>(POST_EVENT_FIELD_KEYS);

export const isCancellationFieldKey = (key: string): boolean => CANCELLATION_KEY_SET.has(key);

export const isPostEventFieldKey = (key: string): boolean => POST_EVENT_KEY_SET.has(key);

export const isLifecycleFieldKey = (key: string): boolean =>
    isCancellationFieldKey(key) || isPostEventFieldKey(key);

export const isCancellationVisibleForStatus = (status: string | null | undefined): boolean =>
    CANCELLED_STATUSES.includes(status ?? '');

export const isPostEventVisibleForStatus = (status: string | null | undefined): boolean =>
    POST_EVENT_STATUSES.includes(status ?? '');

export const isCancellationOtherVisible = (reason: string | null | undefined): boolean =>
    reason === CANCELLATION_REASON_OTHER;

// High Profile Reasoning is hidden (and therefore not required) until High Profile Order is checked.
export const isHighProfileReasoningVisible = (highProfileOrderValue: unknown): boolean =>
    highProfileOrderValue === 'Yes' || highProfileOrderValue === true;

export const isHighProfileReasoningRequired = isHighProfileReasoningVisible;

// Attendee Details also hides whenever its parent Reasoning field hides, i.e. as soon as High Profile Order is unchecked.
export const isHighProfileAttendeeDetailsVisible = (
    reasoning: string | null | undefined,
    highProfileOrderValue: unknown,
): boolean =>
    isHighProfileReasoningVisible(highProfileOrderValue) && reasoning === HIGH_PROFILE_REASONING_ATTENDEE_VALUE;

export const isBillingTimeSensitivityVisible = (billingMethod: string | null | undefined): boolean =>
    billingMethod === BILLING_METHOD_BILL_PER_EVENT;

export const isNoContractRequiredRecordType = (recordType: string | null | undefined): boolean =>
    recordType === NO_CONTRACT_REQUIRED_RECORD_TYPE_VALUE;

export const toAllowedRecordTypeName = (recordType: string | null | undefined): string | null => {
    if (recordType === STANDARD_ORDER_RECORD_TYPE || recordType === NO_CONTRACT_REQUIRED_RECORD_TYPE_VALUE) {
        return recordType;
    }
    return null;
};

export const isContractNumberVisibleForRecordType = (recordType: string | null | undefined): boolean =>
    !isNoContractRequiredRecordType(recordType);

export const isContractNumberRequiredForRecordType = (recordType: string | null | undefined): boolean =>
    !isNoContractRequiredRecordType(recordType);

export const includeContractNumberField = (fieldKey: string, recordType: string | null | undefined): boolean =>
    fieldKey !== CONTRACT_NUMBER_FIELD_KEY || isContractNumberVisibleForRecordType(recordType);

export const isContractNumberHidden = (fieldKey: string, recordType: string | null | undefined): boolean =>
    fieldKey === CONTRACT_NUMBER_FIELD_KEY && !includeContractNumberField(fieldKey, recordType);

export const resolveRecordTypeName = (order: ISalesforceOrderRecordDTO | null | undefined): string | null => {
    const recordType = order?.recordType;
    if (typeof recordType === 'string' && recordType) {
        return recordType;
    }
    if (recordType && typeof recordType === 'object' && recordType.name) {
        return recordType.name;
    }
    return null;
};

export const getRecordTypeFromOrder = (order: ISalesforceOrderRecordDTO | null | undefined): string =>
    resolveRecordTypeName(order) ?? STANDARD_ORDER_RECORD_TYPE;